import type { ProviderKind } from '../config.js';

/**
 * The one interface the sync engine talks to. Every provider — Drive, Graph,
 * Dropbox, WebDAV — converts its own conventions to this at its edge, so the
 * engine never learns which one it is driving. See docs/ARCHITECTURE.md §4.
 *
 * Paths here are always the normalized, POSIX, root-relative form from
 * `paths.ts`: no leading or trailing separator, and the root itself is `''`.
 */

export interface RemoteEntry {
	/** Provider file id; the path itself on path-based providers like WebDAV. */
	remoteId: string;
	path: string;
	kind: 'file' | 'folder';
	/**
	 * etag / rev / cTag / headRevisionId. Opaque: compare for equality only.
	 * Ordering means nothing — a "newer" version can sort lower.
	 */
	version: string;
	/** ISO 8601. */
	modifiedAt: string;
	/**
	 * Bytes. Present on every file entry an adapter hands back — a listing, a
	 * feed, a write, and the `remote` of a `ConflictError` — and absent for
	 * folders. An upload that finds a file already at its name asks whether it
	 * is the same file by its size (docs/ARCHITECTURE.md §7).
	 */
	size?: number;
}

/**
 * What `read`, `move` and `delete` actually need, which is all the local store
 * keeps: a note record carries `remoteId` and `path` and nothing else about the
 * remote entry. Taking the whole `RemoteEntry` would force every caller to
 * invent a `kind` and a `modifiedAt` it does not have, and would let an adapter
 * quietly depend on the invented value.
 *
 * `remoteId` is the identity for id-based providers; on WebDAV it *is* the
 * path, which is why renames there are re-linked through frontmatter `id`.
 */
export type EntryRef = Pick<RemoteEntry, 'remoteId' | 'path'>;

/**
 * A change that removed something. Deliberately not a `RemoteEntry` with a
 * flag: Dropbox's `DeletedMetadata` carries only a path — no id, no rev, no
 * timestamp — so anything richer would have adapters fabricating fields, and
 * the engine trusting them. A deletion is matched by `remoteId` when it has one
 * and by path when it has not.
 *
 * So one of the two is always there, and either may be missing. A feed that
 * names items by id can be asked about a file it never placed — another
 * device's push after this device's cursor, deleted before the next pull — and
 * has no path to give. Dropping that deletion would leave the note here for
 * ever, since the cursor moves on and nothing mentions the file again.
 */
export type DeletedEntry =
	| { path: string; deleted: true; remoteId?: string }
	| { path?: undefined; deleted: true; remoteId: string };

export type ChangeEntry = (RemoteEntry & { deleted?: false }) | DeletedEntry;

export interface ChangeSet {
	entries: readonly ChangeEntry[];
	/** Opaque; persisted per connection, and only after the batch commits. */
	cursor: string;
	more: boolean;
}

export interface WriteOptions {
	/**
	 * Omitting this means "create": a file already at that path is a
	 * `ConflictError`, never a silent overwrite. This is WebDAV's
	 * `If-None-Match: *`, and it is what makes re-creating a note that was
	 * deleted remotely safe — if the file came back in the meantime, the write
	 * conflicts instead of clobbering it.
	 */
	expectedVersion?: string;
}

export interface CreateFileOptions {
	/**
	 * What the bytes are, for a provider that records it (Drive's `mimeType`).
	 * `application/octet-stream` when absent, empty, or anything but a plain
	 * `type/subtype` (`uploadType`). Never used to decide anything here: the
	 * name's extension is what the app reads.
	 */
	contentType?: string;
}

/**
 * Written as property signatures rather than methods to match the house style
 * (`EntitlementProvider`), which `functional/prefer-property-signatures`
 * enforces.
 *
 * Semantics the signatures alone do not carry:
 * - `createFolder` and `delete` are idempotent, so a queued op is always safe
 *   to replay.
 * - The root is a folder that is there, and nothing else: no file is written or
 *   created at it, nothing is moved onto it, and it is never deleted, left or
 *   moved — named by its path or by its `rootId` — each refused with
 *   `NotFoundError` (`outsideRoot` and `refuseRoot` in `adapter.ts`).
 * - `move` may or may not change `version` — Dropbox's `rev` survives a move,
 *   OneDrive's `eTag` does not — so the caller stores the returned entry rather
 *   than assuming either way.
 * - `move` to the path the entry is already at succeeds and changes nothing.
 *   The engine renames on the push path when it finds a rename queued behind a
 *   write, which leaves the queued `move` naming a path the file has already
 *   reached; the queue is ordered and stops on a failed op, so an adapter that
 *   reported this as an error would strand everything behind it. Dropbox's
 *   `files/move_v2` refuses it — with which of `to/conflict`,
 *   `duplicated_or_nested_paths` and `cant_move_folder_into_itself` is not
 *   documented — so its adapter reads the refusal and asks what is actually at
 *   the path, rather than trusting the tag.
 * - `read` fails with `NotFoundError` when there is nothing there, `AuthError`
 *   when the token is the problem, and an untyped error for anything else. Never
 *   `ConflictError` — the engine uses a read as an existence probe and takes a
 *   `NotFoundError` for "gone" and everything else for "could not say", and a
 *   conflict arriving from one would be routed as a conflict on the write.
 *   A file that is there and is not UTF-8 text is an `UnreadableError`, never
 *   text with U+FFFD where the bytes were: that looks like a note, and the next
 *   push writes the damage over the original. A probe takes it for "there".
 * - `list` is one level; `changes` covers the whole tree at every depth.
 * - Neither filters hidden paths: `.notesapp.json` has to reach the engine.
 *   Callers filter with `isHidden`.
 * - `rootId` is opaque, non-empty and stable. A provider whose root has no id
 *   of its own — a Dropbox app folder, where the root simply is `/` — returns a
 *   synthetic constant.
 * - `read` and `write` are for notes: UTF-8 text, decoded strictly
 *   (`decodeText`). `readBytes` and `createFile` are for every other file — an
 *   attachment beside a note (docs/ARCHITECTURE.md §3) — and hand the bytes over
 *   exactly as they are, a BOM included. `read` is `readBytes` decoded, so the
 *   two agree on what is there and on every error but `UnreadableError`, which
 *   `readBytes` never raises.
 * - `createFile` is create-only, and there is no update: the app never writes
 *   into a file that is not a note, and a name stamped with its content
 *   (docs/ARCHITECTURE.md §3) is never asked to hold other bytes. A file *or
 *   folder* already at the path is a `ConflictError` whose `remote` carries its
 *   `kind` and, for a file, its `size` — what lets the caller tell its own
 *   file, uploaded once already, from a different one. A missing parent may be
 *   `NotFoundError` or may be made, as for `write` (Dropbox makes it).
 * - No `AbortSignal`: operations are short, and the engine discards results it
 *   no longer wants. Revisit in Phase 6 if a hung request ever blocks a queue.
 */
export interface StorageProvider {
	readonly kind: ProviderKind;
	/**
	 * Whether `list` reports everything a folder holds.
	 *
	 * False on Google Drive: `drive.file` shows the app only the files it made
	 * itself, so a file the user dropped into a notebook through drive.google.com
	 * is invisible here (docs/ARCHITECTURE.md §5.1), and a listing that comes back empty
	 * is no evidence the folder is. The engine removes a folder on the strength
	 * of a listing (`rmdir`), and will not where this is false, because deleting
	 * a folder takes everything under it — seen or not. It leaves the folder
	 * instead (`leaveFolder`).
	 *
	 * True on Dropbox and OneDrive, which give the app its own folder and
	 * everything in it.
	 */
	readonly listsEverything: boolean;
	/**
	 * Where `listsEverything` is false, what the engine does instead of deleting
	 * a folder it has emptied (`rmdir`): marks the folder as one the app has
	 * left, and touches nothing in it. From then on, while nothing the app can
	 * see is beneath it — no file, and no folder not left too — `changes`
	 * reports it deleted and a scan leaves it out, so no device shows it as an
	 * empty notebook. Anything the app puts beneath it brings it back, and
	 * `createFolder` at its path takes the mark off. `list` still lists it: it
	 * is there, and whatever the user put in it is there too.
	 *
	 * Absent on Dropbox and OneDrive, which delete. On Google Drive the mark is
	 * a private `appProperties` key (docs/ARCHITECTURE.md §5.1).
	 */
	readonly leaveFolder?: (entry: EntryRef) => Promise<void>;
	/**
	 * How many notes a push may write at once; one where absent.
	 *
	 * Four on Google Drive, where a note is three or four requests end to end:
	 * one at a time, an import of a thousand took most of half an hour. Four is
	 * well inside its limits — 325,000 quota units a user a minute, and a note
	 * costs a few hundred
	 * (https://developers.google.com/workspace/drive/api/guides/limits).
	 *
	 * One on Dropbox, which takes a lock on the namespace for each write, so
	 * that enough written at once contend for it and are answered `429
	 * too_many_write_operations`
	 * (https://docs.dropboxapi.com/dropbox-api/docs/performance) — a rate limit,
	 * which stops the drain and backs the scheduler off, so writes sent together
	 * could finish later than in turn. And one on OneDrive until it has
	 * been measured there.
	 */
	readonly writesAtOnce?: number;
	/** Create the app folder if missing; write the marker only if absent. */
	readonly ensureRoot: () => Promise<{ rootId: string }>;
	readonly list: (folderPath: string) => Promise<RemoteEntry[]>;
	readonly read: (entry: EntryRef) => Promise<{ content: string; version: string }>;
	readonly write: (path: string, content: string, opts: WriteOptions) => Promise<RemoteEntry>;
	/** The file's bytes as stored, with the version they are. Never decoded. */
	readonly readBytes: (
		entry: EntryRef
	) => Promise<{ bytes: Uint8Array<ArrayBuffer>; version: string }>;
	/** Create-only, for a file that is not a note; see above. */
	readonly createFile: (
		path: string,
		bytes: Uint8Array<ArrayBuffer>,
		opts?: CreateFileOptions
	) => Promise<RemoteEntry>;
	readonly createFolder: (path: string) => Promise<RemoteEntry>;
	readonly move: (entry: EntryRef, newPath: string) => Promise<RemoteEntry>;
	readonly delete: (entry: EntryRef) => Promise<void>;
	/** No cursor means a full scan of current state, which produces one. */
	readonly changes: (cursor?: string) => Promise<ChangeSet>;
}

/**
 * A provider answered with something this adapter cannot read as the answer
 * it documents: an item with no id, a file with no version, a page with
 * neither link. Deliberately without a `code`, so the engine treats it as the
 * untyped failure it always was — retried, and counted against the op — and
 * named only so that a log, or a test, can tell it from an outage.
 */
export class ProviderResponseError extends Error {
	override readonly name = 'ProviderResponseError';
}

/**
 * Errors carry a `code` as well as their class. `instanceof` is the ergonomic
 * check and works today, since `@skysa/core` resolves to one module per bundle;
 * the code is what keeps the guards below honest if core is ever published and
 * a consumer ends up with two copies of it either side of a sync boundary.
 */
export type ProviderErrorCode =
	'conflict' | 'auth' | 'not-found' | 'cursor-reset' | 'rate-limit' | 'unreadable';

/**
 * The remote moved under us. `remote` is the entry as it exists now, so the
 * conflict rule in docs/ARCHITECTURE.md §7 can write the local copy aside without a
 * second round trip.
 */
export class ConflictError extends Error {
	// A class field rather than `this.name = ...` in the constructor: assigning
	// to `this` is banned, and the field satisfies `noImplicitOverride`.
	override readonly name = 'ConflictError';
	readonly code: ProviderErrorCode = 'conflict';

	constructor(readonly remote: RemoteEntry) {
		super(`conflict at ${remote.path}`);
	}
}

/** The engine responds by requesting a fresh access token from the backend. */
export class AuthError extends Error {
	override readonly name = 'AuthError';
	readonly code: ProviderErrorCode = 'auth';
}

/** The entry is gone, or `expectedVersion` was given for a path with no file. */
export class NotFoundError extends Error {
	override readonly name = 'NotFoundError';
	readonly code: ProviderErrorCode = 'not-found';

	constructor(readonly path: string) {
		super(`not found: ${path}`);
	}
}

/**
 * The file is there and is not a note this app can hold: its bytes are not
 * UTF-8, or they are and the text carries a U+0000 (`decodeText`). Distinct
 * from `NotFoundError`, because the file exists and must be left exactly as it
 * is, and from an untyped failure, because trying again reads the same bytes.
 *
 * `path` is the one the caller asked with. On a read by id that is where the
 * note is, which need not be where the file is, so nothing is decided from it.
 */
export class UnreadableError extends Error {
	override readonly name = 'UnreadableError';
	readonly code: ProviderErrorCode = 'unreadable';

	constructor(readonly path: string) {
		super(`not UTF-8 text: ${path}`);
	}
}

/**
 * The cursor is no longer usable — Dropbox answers `reset`, Graph answers
 * `410 resyncRequired`, WebDAV invalidates its sync-token. Distinct from a
 * transient failure, because the answer is to discard the cursor and re-scan
 * rather than to retry the same call forever.
 */
export class CursorResetError extends Error {
	override readonly name = 'CursorResetError';
	readonly code: ProviderErrorCode = 'cursor-reset';
	constructor(
		message: string,
		/**
		 * The provider says its own copy may be the one that lost something, so
		 * the scan that follows must not be trusted to say what was deleted.
		 * Graph's `resyncChangesUploadDifferences`: "Upload any local items that
		 * the service didn't return". Absent — the ordinary case, and the one an
		 * unrecognised reset falls back to — the scan is the truth and a note it
		 * does not return is gone.
		 * https://learn.microsoft.com/en-us/graph/api/driveitem-delta
		 */
		readonly uploadDifferences?: boolean
	) {
		super(message);
	}
}

/**
 * The provider asked us to slow down: Dropbox and Graph answer 429, Graph also
 * a 503 that says how long to wait (a 503 that doesn't is an outage, not a
 * rate limit), and Drive a 403 whose reason names the quota, or a body whose
 * status is `RESOURCE_EXHAUSTED`. Distinct from a transient
 * failure because it says nothing at all about the request — so the engine does
 * not count it against the op's attempts — and because the provider often says
 * how long to wait, which is better than any backoff we could guess.
 *
 * `retryAfterMs` is absent when the provider said nothing, or said something
 * unreadable. The caller then uses its own backoff.
 */
export class RateLimitError extends Error {
	override readonly name = 'RateLimitError';
	readonly code: ProviderErrorCode = 'rate-limit';

	constructor(
		message: string,
		readonly retryAfterMs?: number
	) {
		super(message);
	}
}

const hasCode = (error: unknown, code: ProviderErrorCode): boolean =>
	typeof error === 'object' && error !== null && (error as { code?: unknown }).code === code;

export const isConflictError = (error: unknown): error is ConflictError =>
	error instanceof ConflictError || hasCode(error, 'conflict');

export const isAuthError = (error: unknown): error is AuthError =>
	error instanceof AuthError || hasCode(error, 'auth');

export const isNotFoundError = (error: unknown): error is NotFoundError =>
	error instanceof NotFoundError || hasCode(error, 'not-found');

export const isCursorResetError = (error: unknown): error is CursorResetError =>
	error instanceof CursorResetError || hasCode(error, 'cursor-reset');

export const isRateLimitError = (error: unknown): error is RateLimitError =>
	error instanceof RateLimitError || hasCode(error, 'rate-limit');

export const isUnreadableError = (error: unknown): error is UnreadableError =>
	error instanceof UnreadableError || hasCode(error, 'unreadable');
